import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import grafana from "./images/grafana.svg";
import infoIcon from "./images/info.svg";
import {
  listAllDesktops,
  listMyDesktops,
  desktopUpdate,
  desktopDelete,
  fetchPersistProgress,
} from "../slices/desktopSlice";
import { listTemplates, listFlavors, listOSs } from "../slices/templateSlice";
import { userAccountDetails, logout } from "../slices/keycloakSlice";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./Common.css";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { FaDesktop, FaUser } from "react-icons/fa";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import StopIcon from "@mui/icons-material/Stop";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { web_client_url, api_url } from "../config.js";
import { isMobileDevice } from "../utils/deviceUtils";

const formatBytes = (bytes) => {
  if (bytes === 0 || !bytes) return "0 B";
  const k = 1000;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

function HomePage({ envIsNoAuth }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const keycloak = JSON.parse(localStorage.getItem("keycloak") || "{}");

  const { userInfo, loading } = useSelector((state) => state.user);

  const { desktops: assignedDesktopsList } = useSelector(
    (state) => state.desktop,
  );

  // Fetching OS List for dynamic icons
  const { oss: osList } = useSelector((state) => state.template);

  const [isNativeSPICE, setNativeSPICE] = useState(false);
  const [desktopToOpen, setDesktopToOpen] = useState(null);
  const [rememberChoice, setRememberChoice] = useState(false);
  const [persistStatuses, setPersistStatuses] = useState({});

  useEffect(() => {
    if (!envIsNoAuth) {
      if (keycloak?.authenticated && !userInfo) {
        dispatch(userAccountDetails(keycloak?.token));
      } else if (!keycloak?.authenticated) {
        navigate("/"); // home
      } else {
        dispatch(listTemplates());
        dispatch(listFlavors());
        dispatch(listOSs());
      }
    } else {
      dispatch(listTemplates());
      dispatch(listFlavors());
      dispatch(listOSs());
    }
  }, [
    envIsNoAuth,
    dispatch,
    userInfo,
    keycloak?.authenticated,
    keycloak?.token,
    navigate,
  ]);

  const [filteredDesktops, setFilteredDesktops] = useState([]);
  const [showAllDesktops, setShowAllDesktops] = useState(() => {
    const savedPref = localStorage.getItem("desktopFilterPreference");
    return savedPref ? savedPref === "all" : false;
  });

  useEffect(() => {
    if (showAllDesktops || envIsNoAuth) {
      setFilteredDesktops(
        Array.isArray(assignedDesktopsList) ? assignedDesktopsList : [],
      );
    } else {
      const filtered = Array.isArray(assignedDesktopsList)
        ? assignedDesktopsList.filter(
            (desktop) => desktop.ownerId === userInfo.id,
          )
        : [];
      setFilteredDesktops(filtered);
    }
  }, [assignedDesktopsList, showAllDesktops, envIsNoAuth, userInfo?.id]);

  const handleFilterDesktops = () => {
    const nextState = !showAllDesktops;
    setShowAllDesktops(nextState);
    localStorage.setItem("desktopFilterPreference", nextState ? "all" : "mine");

    if (nextState || envIsNoAuth) {
      setFilteredDesktops(assignedDesktopsList);
    } else {
      const filtered = Array.isArray(assignedDesktopsList)
        ? assignedDesktopsList.filter(
            (desktop) => desktop.ownerId === userInfo.id,
          )
        : [];
      setFilteredDesktops(filtered);
    }
  };

  // View Selection
  const [viewType, setViewType] = useState(() => {
    return localStorage.getItem("desktopViewPreference") || "grid";
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: null,
  });
  const [statusFilter, setStatusFilter] = useState("all");

  const baseDesktops = useMemo(() => {
    let result = [...filteredDesktops].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );
    if (searchQuery) {
      result = result.filter((d) =>
        (d.name || "").toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    if (statusFilter === "running") {
      result = result.filter((d) => d.state === "Running");
    } else if (statusFilter === "stopped") {
      result = result.filter((d) => d.state === "Stopped");
    }
    return result;
  }, [filteredDesktops, searchQuery, statusFilter]);

  const baseDesktopsRef = useRef(baseDesktops);
  useEffect(() => {
    baseDesktopsRef.current = baseDesktops;
  }, [baseDesktops]);

  useEffect(() => {
    const fetchProgress = async () => {
      const persistingDesktops = baseDesktopsRef.current.filter(
        (d) => d.state === "Persisting",
      );

      if (persistingDesktops.length === 0) return;

      const newStatuses = {};
      for (const d of persistingDesktops) {
        try {
          const data = await dispatch(fetchPersistProgress(d.id)).unwrap();
          if (Array.isArray(data)) {
            newStatuses[d.id] = data;
          }
        } catch (err) {
          // change to silent catch?
          toast.error(
            typeof err === "string"
              ? err
              : err?.detail || "Failed to get persistance updates.",
          );
        }
      }

      if (Object.keys(newStatuses).length > 0) {
        setPersistStatuses((prev) => ({ ...prev, ...newStatuses }));
      }
    };

    fetchProgress();
    const interval = setInterval(fetchProgress, 3000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const getCombinedProgress = (desktopId) => {
    const data = persistStatuses[desktopId];
    if (!data || data.length === 0) return null;
    let current = 0;
    let total = 0;
    let bandwidth = 0;
    data.forEach((item) => {
      current += item.currentBytes || 0;
      total += item.totalBytes || 0;
      bandwidth += item.bandwidth || 0;
    });
    const percent = total > 0 ? (current / total) * 100 : 0;
    return { current, total, percent, bandwidth };
  };

  const logoutHandler = () => {
    dispatch(logout());
  };

  // Dynamic OS Icon Logic
  const getOsIconSrc = (desktop) => {
    let matchedOs = null;

    if (desktop.operatingSystem && desktop.operatingSystem.iconUri) {
      matchedOs = desktop.operatingSystem;
    } else if (osList?.length > 0) {
      if (desktop.operatingSystemId) {
        matchedOs = osList.find(
          (os) =>
            String(os.id).toLowerCase() ===
            String(desktop.operatingSystemId).toLowerCase(),
        );
      } else if (desktop.os) {
        const osName =
          typeof desktop.os === "object" ? desktop.os.name : desktop.os;
        matchedOs = osList.find(
          (os) =>
            String(os.name).toLowerCase() === String(osName).toLowerCase(),
        );
      }
    }

    if (matchedOs?.iconUri) {
      const uri = matchedOs.iconUri;
      if (uri.startsWith("http") || uri.startsWith("data:")) {
        return uri;
      } else {
        const cleanBase = api_url.replace(/\/$/, "");
        const cleanPath = uri.startsWith("/") ? uri : `/${uri}`;
        return `${cleanBase}${cleanPath}`;
      }
    }
    return null;
  };

  const getOsName = (desktop) => {
    if (desktop.operatingSystem?.name) return desktop.operatingSystem.name;
    if (osList?.length > 0) {
      let matchedOs = null;
      if (desktop.operatingSystemId) {
        matchedOs = osList.find(
          (os) =>
            String(os.id).toLowerCase() ===
            String(desktop.operatingSystemId).toLowerCase(),
        );
      } else if (desktop.os) {
        const osName =
          typeof desktop.os === "object" ? desktop.os.name : desktop.os;
        matchedOs = osList.find(
          (os) =>
            String(os.name).toLowerCase() === String(osName).toLowerCase(),
        );
      }
      if (matchedOs?.name) return matchedOs.name;
    }
    if (desktop.os)
      return typeof desktop.os === "object"
        ? desktop.os.name || ""
        : desktop.os;
    return "";
  };

  const handleSortClick = (field) => {
    setSortConfig((prev) => {
      if (prev.field !== field) return { field, direction: "asc" };
      if (prev.direction === "asc") return { field, direction: "desc" };
      return { field: null, direction: null };
    });
  };

  const sortIndicator = (field) => {
    if (sortConfig.field !== field) return " ↕";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  useEffect(() => {
    const baseSearchParams = new URLSearchParams(window.location.search);
    // Check the proper React Router Hash location (after the hash)
    const hashSearchParams = new URLSearchParams(location.search);

    // Look for IDs and names in either location
    const templateIdFromUrl =
      baseSearchParams.get("templateId") ||
      hashSearchParams.get("templateId") ||
      baseSearchParams.get("id") ||
      hashSearchParams.get("id");

    const nameFromUrl =
      baseSearchParams.get("name") || hashSearchParams.get("name");

    const openModal =
      baseSearchParams.get("openModal") === "true" ||
      hashSearchParams.get("openModal") === "true";

    if (openModal || templateIdFromUrl) {
      // Clear the hash to prevent infinite loops if the user goes back
      if (window.location.search || window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname);
      }

      // pass both the template ID and the name down to the new route
      const destParams = new URLSearchParams();
      if (templateIdFromUrl) destParams.set("templateId", templateIdFromUrl);
      if (nameFromUrl) destParams.set("name", nameFromUrl);

      navigate(`/create-desktop?${destParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.search, location.hash, navigate]);

  const desktopStatus = {
    Running: { color: "#28a745", label: "Running", blink: false },
    Stopped: { color: "#dc3545", label: "Killed", blink: false },
    Unknown: { color: "#6c757d", label: "Unknown", blink: false },
    Crashed: { color: "#ffc107", label: "Crashed", blink: false },
    Creating: { color: "#28a745", label: "Creating", blink: true },
    Deleting: { color: "#dc3545", label: "Deleting", blink: true },
    Persisting: { color: "#007bff", label: "Persisting", blink: true },
  };

  const handleDesktopAction = (desktopId, action) => {
    // power off with dark red toast
    if (action === "Destroy") {
      if (
        window.confirm(
          "Are you sure you want to permanently delete this desktop?",
        )
      ) {
        dispatch(desktopDelete(desktopId))
          .unwrap()
          .then(() => {
            toast.error("Desktop successfully destroyed", {
              style: { backgroundColor: "#721c24", color: "white" }, // dark red toast
            });
            // refresh the list so it's greyed out until the backend fully removes the record
          })
          .catch((err) => toast.error(err.detail || "Deletion failed")); // extract error from backend
      }
      return;
    }

    // handling state changes (Start, Stop, Restart, Shut Down, Power OFF)
    dispatch(
      desktopUpdate({
        id: desktopId,
        desktopData: { pendingAction: action },
      }),
    )
      .unwrap()
      .then(() => {
        // green toast for "Creating" (Start) or other actions
        if (action === "Start") {
          toast.success("Desktop creation/start initiated", {
            style: { backgroundColor: "#28a745", color: "white" },
          });
        } else {
          toast.success(`${action} action successful`);
        }
      })
      .catch((err) => {
        // error: show title and detail from backend
        const errorTitle = `Failed to ${action}`;
        const errorDetail =
          typeof err === "string"
            ? err
            : err.detail || "An unexpected error occurred.";

        toast.error(
          <div>
            <strong>{errorTitle}</strong>
            <div style={{ fontSize: "12px", marginTop: "4px" }}>
              {errorDetail}
            </div>
          </div>,
        );
      });
  };

  const executeOpenDesktopAction = (desktop, preference, event) => {
    let targetUrl;
    if (preference === "html") {
      targetUrl = `${web_client_url}${desktop.url}`;
    } else {
      targetUrl = desktop.url;
    }

    // check if Ctrl (Windows/Linux) or Cmd (Mac) was held down during the click
    const isCtrlPressed = event && (event.ctrlKey || event.metaKey);

    if (isCtrlPressed) {
      window.open(targetUrl, "_blank");
    } else {
      window.location = targetUrl;
    }
  };

  const handleSaveDesktopPreferenceAndOpenDesktop = (method, event) => {
    if (rememberChoice) {
      localStorage.setItem("desktopClientPreference", method);
    }
    setNativeSPICE(false);
    executeOpenDesktopAction(desktopToOpen, method, event);
  };

  const handleOpenDesktop = (desktop, event) => {
    if (desktop.state === "Persisting") {
      toast.warning("Actions are disabled while the desktop is persisting.");
      return;
    }

    if (!desktop.url) {
      toast.error("Desktop URL is not available yet.");
      return;
    }

    // VM is not running => start it
    if (desktop.state !== "Running") {
      toast.info("Starting VM... please wait a moment before opening.");
      handleDesktopAction(desktop.id, "Start");
      return;
    }

    // If it is on a phone/tablet, then force html, otherwise read local storage
    const preference = isMobileDevice
      ? "html"
      : localStorage.getItem("desktopClientPreference") || "ask";

    if (preference === "html" || preference === "native") {
      executeOpenDesktopAction(desktop, preference, event);
    } else {
      // Trigger "Ask Me" modal for desktop users who haven't saved a preference
      setRememberChoice(false);
      setDesktopToOpen(desktop);
      setNativeSPICE(true);
    }
  };

  const renderToolbar = () => (
    <div className="hp-top-bar">
      <h2 className="hp-title">
        {filteredDesktops.length === 0
          ? "No desktops yet, create one!"
          : baseDesktops.length === 0
            ? "No desktops match your filters."
            : "Available desktops:"}
      </h2>

      <div className="hp-top-bar-controls">
        <input
          type="text"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="hp-search-input"
        />

        {userInfo?.isAdmin && (
          <div className="hp-pill-group">
            <button
              className={showAllDesktops ? "active" : ""}
              onClick={() => {
                if (!showAllDesktops) handleFilterDesktops();
              }}
            >
              All
            </button>
            <button
              className={!showAllDesktops ? "active" : ""}
              onClick={() => {
                if (showAllDesktops) handleFilterDesktops();
              }}
            >
              Mine
            </button>
          </div>
        )}

        <div className="hp-pill-group">
          <button
            className={statusFilter === "all" ? "active" : ""}
            onClick={() => setStatusFilter("all")}
          >
            All
          </button>
          <button
            className={statusFilter === "running" ? "active" : ""}
            onClick={() => setStatusFilter("running")}
          >
            Running
          </button>
          <button
            className={statusFilter === "stopped" ? "active" : ""}
            onClick={() => setStatusFilter("stopped")}
          >
            Stopped
          </button>
        </div>

        <div className="hp-pill-group">
          <button
            className={viewType === "grid" ? "active" : ""}
            onClick={() => {
              setViewType("grid");
              localStorage.setItem("desktopViewPreference", "grid");
            }}
            title="Grid view"
          >
            <ViewModuleIcon
              style={{ fontSize: "18px", verticalAlign: "middle" }}
            />
          </button>
          <button
            className={viewType === "table" ? "active" : ""}
            onClick={() => {
              setViewType("table");
              localStorage.setItem("desktopViewPreference", "table");
            }}
            title="Table view"
          >
            <TableRowsIcon
              style={{ fontSize: "18px", verticalAlign: "middle" }}
            />
          </button>
        </div>

        <button
          onClick={() => navigate("/create-desktop")}
          className="hp-create-btn"
        >
          + Create Desktop
        </button>

        {userInfo?.isAdmin && (
          <a
            href="/grafana"
            style={{ display: "flex", alignItems: "center" }}
            title="Grafana"
          >
            <img
              src={grafana}
              alt="Grafana"
              style={{ width: "32px", height: "32px" }}
            />
          </a>
        )}
      </div>
    </div>
  );

  const renderDesktopsGridView = () => (
    <div className="hp-desktop-area">
      <div className="desktop-card-wrapper">
        {baseDesktops.map((desktop, i) => {
          const iconSrc = getOsIconSrc(desktop);
          const status = desktop.state || "Unknown";
          const config = desktopStatus[status] || desktopStatus.Unknown;
          const isDesktopBusy = status === "Creating" || status === "Deleting";

          return (
            <div
              className={`desktop-card border ${isDesktopBusy ? "card-busy" : ""}`}
              key={i}
              onClick={(event) => handleOpenDesktop(desktop, event)}
              style={{
                filter: isDesktopBusy ? "grayscale(0.5) opacity(0.8)" : "none",
                pointerEvents: isDesktopBusy ? "none" : "auto",
                cursor: status === "Persisting" ? "not-allowed" : "pointer",
              }}
              title={
                status === "Persisting"
                  ? "Actions disabled while persisting"
                  : "Click to open desktop"
              }
            >
              <button
                onClick={(event) => (
                  event.stopPropagation(),
                  navigate(`/desktops/${desktop.id}`)
                )}
                className="info-btn-hitbox"
                title="Click for more info"
              >
                <img
                  src={infoIcon}
                  alt="Info"
                  style={{ width: "20px", height: "20px" }}
                />
              </button>

              <div
                style={{
                  textDecoration: "none",
                  color: "darkblue",
                  cursor: status === "Persisting" ? "not-allowed" : "pointer",
                  paddingRight: "36px",
                  paddingLeft: "4px",
                  textAlign: "center",
                  marginTop: "6px",
                }}
                onClick={(event) => (
                  event.stopPropagation(),
                  handleOpenDesktop(desktop, event)
                )}
                title={desktop.name}
              >
                <h1 className="desktop-name">{desktop.name}</h1>
              </div>

              {/* DESKTOP CARD */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                {/* Left Side: OS icon only */}
                <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
                  <div
                    style={{
                      position: "relative",
                      width: "76px",
                      height: "76px",
                      margin: "0 auto",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {/* OS Icon */}
                    {iconSrc ? (
                      <img
                        src={iconSrc}
                        alt={
                          typeof desktop.os === "object"
                            ? desktop.os?.name
                            : desktop.os || "OS"
                        }
                        style={{
                          width: "58px",
                          height: "58px",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <FaDesktop size={54} color="#444444" />
                    )}
                  </div>
                </div>

                {/* Right Side: Owner, Status, and optionally Persistence progress Bar */}
                <div
                  style={{
                    flex: 1,
                    paddingLeft: "10px",
                    marginTop: "6px",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <p
                    className="desktop-owner"
                    style={{
                      color:
                        desktop.ownerId === userInfo?.id ? "#CC5500" : "black",
                      marginBottom: 0,
                    }}
                    title={`Owner: ${desktop.ownerId}`}
                  >
                    <FaUser
                      style={{
                        verticalAlign: "middle",
                        marginRight: "5px",
                        marginBottom: "3px",
                      }}
                    />
                    {desktop.ownerId}
                  </p>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginTop: "4px",
                    }}
                    title={`Status: ${desktop.state}`}
                  >
                    <span
                      style={{
                        color: config.color,
                        fontSize: "13px",
                      }}
                    >
                      {desktop.state || "Unknown"}
                    </span>
                    <div
                      className={config.blink ? "status-dot-blink" : ""}
                      style={{
                        width: "9px",
                        height: "9px",
                        borderRadius: "50%",
                        backgroundColor: config.color,
                        border: "2px solid white",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                        flexShrink: 0,
                      }}
                    />
                  </div>

                  {/* IN USE Indicator */}
                  {desktop.currentConnections > 0 && (
                    <div style={{ marginTop: "4px" }}>
                      <span className="in-use-badge">IN USE</span>
                    </div>
                  )}

                  {/* Persistence progress Bar */}
                  {desktop.state === "Persisting" &&
                    (() => {
                      const prog = getCombinedProgress(desktop.id);
                      if (!prog) return null;
                      return (
                        <div className="hp-persist-wrapper">
                          <div className="hp-persist-track">
                            <div
                              className="hp-persist-bar"
                              style={{ width: `${prog.percent}%` }}
                            />
                          </div>
                          <div className="hp-persist-info">
                            <span className="hp-persist-bytes">
                              {formatBytes(prog.current)} /{" "}
                              {formatBytes(prog.total)}
                            </span>
                            <span className="hp-persist-speed">
                              {formatBytes(prog.bandwidth)}/s
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                </div>
              </div>

              {/* Action buttons */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "5px",
                  marginTop: "auto",
                  marginBottom: "5px",
                  backgroundColor: "#ac97c0",
                  borderRadius: "20px",
                  padding: "2px",
                  flexShrink: 0,
                  minWidth: "fit-content",
                  alignSelf: "center",
                  width: "fit-content",
                  opacity: status === "Persisting" ? 0.5 : 1,
                  pointerEvents: status === "Persisting" ? "none" : "auto",
                  filter: status === "Persisting" ? "grayscale(1)" : "none",
                }}
              >
                <button
                  onClick={(event) => (
                    event.stopPropagation(),
                    handleOpenDesktop(desktop, event)
                  )}
                  className="player-btn"
                  title="Start"
                >
                  <PlayArrowIcon fontSize="small" />
                </button>
                <button
                  onClick={(event) => (
                    event.stopPropagation(),
                    handleDesktopAction(desktop.id, "Restart")
                  )}
                  className="player-btn"
                  title="Restart"
                >
                  <ReplayIcon fontSize="small" />
                </button>
                <button
                  onClick={(ev) => (
                    ev.stopPropagation(),
                    handleDesktopAction(desktop.id, "Stop")
                  )}
                  className="player-btn"
                  title="Stop"
                >
                  <StopIcon fontSize="small" />
                </button>
                <button
                  onClick={(ev) => (
                    ev.stopPropagation(),
                    handleDesktopAction(desktop.id, "Kill")
                  )}
                  className="player-btn"
                  title="Power off forcefully"
                >
                  <PowerOffIcon fontSize="small" />
                </button>
                <button
                  onClick={(ev) => (
                    ev.stopPropagation(),
                    handleDesktopAction(desktop.id, "Destroy")
                  )}
                  className="player-btn destroy"
                  title="Delete permanently"
                >
                  <DeleteForeverIcon fontSize="small" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderDesktopsTableView = () => {
    const displayDesktops = sortConfig.field
      ? [...baseDesktops].sort((a, b) => {
          let aVal = "";
          let bVal = "";
          if (sortConfig.field === "name") {
            aVal = a.name || "";
            bVal = b.name || "";
          } else if (sortConfig.field === "os") {
            aVal = getOsName(a);
            bVal = getOsName(b);
          } else if (sortConfig.field === "user") {
            aVal = a.ownerId || "";
            bVal = b.ownerId || "";
          }
          const cmp = aVal.localeCompare(bVal);
          return sortConfig.direction === "asc" ? cmp : -cmp;
        })
      : baseDesktops;

    return (
      <div className="hp-desktop-area">
        <div className="table-responsive-wrapper">
          {displayDesktops.length > 0 && (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "700px",
              }}
            >
              <thead>
                <tr style={{ color: "white" }}>
                  <th
                    onClick={() => handleSortClick("name")}
                    style={{
                      padding: "10px",
                      backgroundColor: "#6551AD",
                      border: "1px solid #ccc",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    Desktop Name{sortIndicator("name")}
                  </th>
                  <th
                    onClick={() => handleSortClick("os")}
                    style={{
                      padding: "10px",
                      backgroundColor: "#6551AD",
                      border: "1px solid #ccc",
                      width: "130px",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    OS{sortIndicator("os")}
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      backgroundColor: "#6551AD",
                      border: "1px solid #ccc",
                    }}
                  >
                    Description
                  </th>
                  <th
                    onClick={() => handleSortClick("user")}
                    style={{
                      padding: "10px",
                      backgroundColor: "#6551AD",
                      border: "1px solid #ccc",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    User{sortIndicator("user")}
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      backgroundColor: "#6551AD",
                      border: "1px solid #ccc",
                      width: "1%",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      backgroundColor: "#6551AD",
                      border: "1px solid #ccc",
                      width: "1%",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayDesktops.map((desktop, i) => {
                  const iconSrc = getOsIconSrc(desktop);
                  const status = desktop.state || "Unknown";
                  const config = desktopStatus[status] || desktopStatus.Unknown;
                  const isBusy = status === "Creating" || status === "Deleting";
                  return (
                    <tr
                      key={i}
                      style={{
                        backgroundColor:
                          i % 2 === 1 ? "#DAD9F1" : "transparent",
                        opacity: isBusy ? 0.5 : 1,
                        pointerEvents: isBusy ? "none" : "auto",
                        filter: isBusy ? "grayscale(1)" : "none",
                      }}
                    >
                      <td
                        style={{
                          padding: "10px",
                          border: "1px solid #ccc",
                          maxWidth: "200px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <Link
                          to={`/desktops/${desktop.id}`}
                          style={{ textDecoration: "none", color: "darkblue" }}
                          title="Click for more info"
                        >
                          {desktop.name}
                        </Link>
                      </td>
                      <td
                        style={{
                          padding: "5px",
                          textAlign: "center",
                          border: "1px solid #ccc",
                        }}
                      >
                        <div
                          onClick={(event) => handleOpenDesktop(desktop, event)}
                          style={{
                            cursor:
                              status === "Persisting"
                                ? "not-allowed"
                                : "pointer",
                            display: "flex",
                            justifyContent: "center",
                          }}
                          title={
                            status === "Persisting"
                              ? "Persisting..."
                              : "Click to open desktop"
                          }
                        >
                          {/* OS Icon */}
                          {iconSrc ? (
                            <img
                              src={iconSrc}
                              alt="OS Icon"
                              style={{
                                width: "30px",
                                height: "30px",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <FaDesktop size={30} color="#444444" />
                          )}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          border: "1px solid #ccc",
                          maxWidth: "200px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {desktop.description}
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          border: "1px solid #ccc",
                          color:
                            desktop.ownerId === userInfo.id
                              ? "orange"
                              : "black",
                          maxWidth: "180px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {desktop.ownerId}
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          border: "1px solid #ccc",
                          verticalAlign: "middle",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                          title={`Status: ${desktop.state}`}
                        >
                          <span style={{ color: config.color }}>
                            {desktop.state || "Unknown"}
                          </span>
                          <div
                            className={config.blink ? "status-dot-blink" : ""}
                            style={{
                              width: "14px",
                              height: "14px",
                              borderRadius: "50%",
                              backgroundColor: config.color,
                              border: "2px solid white",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                            }}
                          />
                        </div>

                        {/* IN USE Indicator */}
                        {desktop.currentConnections > 0 && (
                          <div style={{ marginTop: "3px" }}>
                            <span
                              className="in-use-badge"
                              style={{ fontSize: "14px" }}
                            >
                              IN USE
                            </span>
                          </div>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          border: "1px solid #ccc",
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        }}
                      >
                        {/* Action buttons */}
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            justifyContent: "center",
                            alignItems: "center",
                            opacity: status === "Persisting" ? 0.5 : 1,
                            pointerEvents:
                              status === "Persisting" ? "none" : "auto",
                            filter:
                              status === "Persisting" ? "grayscale(1)" : "none",
                          }}
                        >
                          <button
                            onClick={(event) =>
                              handleOpenDesktop(desktop, event)
                            }
                            className="table-btn"
                            title="Start"
                          >
                            <PlayArrowIcon style={{ fontSize: "24px" }} />
                          </button>
                          <button
                            onClick={() =>
                              handleDesktopAction(desktop.id, "Restart")
                            }
                            className="table-btn"
                            title="Restart"
                          >
                            <ReplayIcon style={{ fontSize: "24px" }} />
                          </button>
                          <button
                            onClick={() =>
                              handleDesktopAction(desktop.id, "Stop")
                            }
                            className="table-btn"
                            title="Stop"
                          >
                            <StopIcon style={{ fontSize: "24px" }} />
                          </button>
                          <button
                            onClick={() =>
                              handleDesktopAction(desktop.id, "Kill")
                            }
                            className="table-btn"
                            title="Power off forcefully"
                          >
                            <PowerOffIcon style={{ fontSize: "24px" }} />
                          </button>
                          <button
                            onClick={() =>
                              handleDesktopAction(desktop.id, "Destroy")
                            }
                            className="table-btn destroy"
                            title="Delete permanently"
                          >
                            <DeleteForeverIcon style={{ fontSize: "24px" }} />
                          </button>
                        </div>

                        {/* Persistence progress Bar */}
                        {desktop.state === "Persisting" &&
                          (() => {
                            const prog = getCombinedProgress(desktop.id);
                            if (!prog) return null;
                            return (
                              <div className="hp-persist-wrapper table-view">
                                <div className="hp-persist-track">
                                  <div
                                    className="hp-persist-bar"
                                    style={{ width: `${prog.percent}%` }}
                                  />
                                </div>
                                <div className="hp-persist-info">
                                  <span className="hp-persist-bytes">
                                    {formatBytes(prog.current)} /{" "}
                                    {formatBytes(prog.total)}
                                  </span>
                                  <span className="hp-persist-speed">
                                    {formatBytes(prog.bandwidth)}/s
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;

  if (!userInfo) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f3f3f3", // light blue page
        }}
      >
        <div
          style={{
            padding: "30px",
            backgroundColor: "#b1cdec",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            marginBottom: "15px",
            textAlign: "center",
            fontSize: "22px",
            color: "#333",
          }}
        >
          No user information found
          <div style={{ marginTop: "15px" }}>
            <Link
              className="nav-link"
              to="/"
              onClick={logoutHandler}
              style={{
                padding: "11px 22px",
                backgroundColor: "#64829e",
                color: "white",
                fontSize: "17px",
                borderRadius: "5px",
                textDecoration: "none",
                textAlign: "center",
                display: "inline-block",
                boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.2)",
                transition: "background-color 0.3s ease",
              }}
              onMouseEnter={(e) =>
                (e.target.style.backgroundColor = "darkblue")
              }
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#64829e")}
            >
              Retry Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <Header />

      {assignedDesktopsList && (
        <>
          {renderToolbar()}
          {viewType === "grid"
            ? renderDesktopsGridView()
            : renderDesktopsTableView()}
        </>
      )}

      {/* "Ask Me" Client Selection Modal */}
      <Modal show={isNativeSPICE} onHide={() => setNativeSPICE(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Choose Client</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="mb-4">How would you like to open this desktop?</p>
          <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
            <Button
              variant="outline-primary"
              onClick={(event) => {
                handleSaveDesktopPreferenceAndOpenDesktop("html", event);
              }}
            >
              Open in Browser
            </Button>
            <Button
              variant="outline-primary"
              onClick={(event) => {
                handleSaveDesktopPreferenceAndOpenDesktop("native", event);
              }}
            >
              Open in Native App
            </Button>
          </div>

          <div className="mt-4 d-flex justify-content-center align-items-center">
            <input
              type="checkbox"
              id="rememberChoice"
              checked={rememberChoice}
              onChange={(e) => setRememberChoice(e.target.checked)}
              style={{ marginRight: "8px", cursor: "pointer" }}
            />
            <label
              htmlFor="rememberChoice"
              style={{
                margin: 0,
                cursor: "pointer",
                fontSize: "0.95em",
                color: "#444",
              }}
            >
              Save this preference as your default one.
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <small className="text-muted">
            You can change your default preference in Account Settings.
          </small>
        </Modal.Footer>
      </Modal>

      <Footer />
    </div>
  );
}

export default HomePage;
